import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";

import { Entity } from "@/jenesys-types/data/common";
import { DataType } from "@/jenesys-types/data/enums";

import { selectAllByDataType } from "@/store/data/data";

export interface EntityNavigationState {
  focusedEntityId?: string;
  dataType?: DataType;
  sortField?: string;
  sortDirection?: "asc" | "desc";
}

const initialState: EntityNavigationState = {};

export const entityNavigationSlice = createSlice({
  name: "entityNavigation",
  initialState,
  reducers: {
    setFocusedEntity: (
      state,
      action: PayloadAction<{
        id: string;
        dataType: DataType;
        sortField?: string;
        sortDirection?: "asc" | "desc";
      }>
    ) => {
      state.focusedEntityId = action.payload.id;
      state.dataType = action.payload.dataType;
      // Optional sort parameters
      if (action.payload.sortField) {
        state.sortField = action.payload.sortField;
      }
      if (action.payload.sortDirection) {
        state.sortDirection = action.payload.sortDirection;
      }
    },
    clearFocusedEntity: (state) => {
      state.focusedEntityId = undefined;
      state.dataType = undefined;
    }
  }
});

export const { setFocusedEntity, clearFocusedEntity } =
  entityNavigationSlice.actions;

// Selectors
export const selectEntityNavigationState = (state: {
  entityNavigation: EntityNavigationState;
}) => state.entityNavigation;

export const selectFocusedEntityData = createSelector(
  selectEntityNavigationState,
  (state) => ({
    id: state.focusedEntityId,
    dataType: state.dataType,
    sortField: state.sortField,
    sortDirection: state.sortDirection
  })
);

export const selectFocusedEntityId = createSelector(
  selectEntityNavigationState,
  (state) => state.focusedEntityId
);

// Helper function to get value from an object by path
const getNestedValue = (obj: any, path: string): any =>
  // Split the path into parts and reduce through the object
  path
    .split(".")
    .reduce(
      (current, key) =>
        current && current[key] !== undefined ? current[key] : undefined,
      obj
    );
// Helper function to sort entities if sort parameters are provided
const getSortedEntities = <T extends Entity>(
  entities: Array<T>,
  sortField?: string,
  sortDirection?: "asc" | "desc"
): T[] => {
  if (!sortField) return entities;

  return [...entities].sort((a, b) => {
    const aVal = getNestedValue(a, sortField);
    const bVal = getNestedValue(b, sortField);

    // Handle undefined/null values
    if (aVal === undefined && bVal === undefined) return 0;
    if (aVal === undefined) return 1;
    if (bVal === undefined) return -1;

    // Compare values
    let compare;
    if (aVal < bVal) {
      compare = -1;
    } else if (aVal > bVal) {
      compare = 1;
    } else {
      compare = 0;
    }

    return sortDirection === "desc" ? -compare : compare;
  });
};

// Create a selector to get the neighboring entities
export const makeSelectNeighboringEntities = () =>
  createSelector(
    [selectFocusedEntityData, (_state: any, entities: Entity[]) => entities],
    ({ id, sortField, sortDirection }, entities) => {
      if (!id || entities.length === 0) {
        return { previousEntity: undefined, nextEntity: undefined };
      }

      // Sort the entities if needed
      const sortedEntities = getSortedEntities(
        entities,
        sortField,
        sortDirection
      );

      // Find the index of the current entity
      const currentIndex = sortedEntities.findIndex(
        (entity) => entity.id === id
      );

      // If the entity is not found in the list, return undefined for both neighbors
      if (currentIndex === -1) {
        return { previousEntity: undefined, nextEntity: undefined };
      }

      // Get the previous and next entities
      const previousEntity =
        currentIndex > 0 ? sortedEntities[currentIndex - 1] : undefined;
      const nextEntity =
        currentIndex < sortedEntities.length - 1
          ? sortedEntities[currentIndex + 1]
          : undefined;

      return { previousEntity, nextEntity };
    }
  );

// Selector to get the neighboring entities for the currently focused entity
export const selectNeighboringEntities = createSelector(
  [
    selectFocusedEntityData,
    (state) => {
      const { dataType } = state.entityNavigation;
      return dataType ? selectAllByDataType(dataType)(state) : [];
    }
  ],
  ({ id, sortField, sortDirection }, entities) => {
    if (!id || entities.length === 0) {
      return { previousEntity: undefined, nextEntity: undefined };
    }

    // Sort the entities if needed
    const sortedEntities = getSortedEntities(
      entities,
      sortField,
      sortDirection
    );

    // Find the index of the current entity
    const currentIndex = sortedEntities.findIndex((entity) => entity.id === id);

    // If the entity is not found in the list, return undefined for both neighbors
    if (currentIndex === -1) {
      return { previousEntity: undefined, nextEntity: undefined };
    }

    // Get the previous and next entities
    const previousEntity =
      currentIndex > 0 ? sortedEntities[currentIndex - 1] : undefined;
    const nextEntity =
      currentIndex < sortedEntities.length - 1
        ? sortedEntities[currentIndex + 1]
        : undefined;

    return { previousEntity, nextEntity };
  }
);

export default entityNavigationSlice.reducer;
